// src/components/sections/Services/ProjectGallery.jsx

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

import {
  FaChevronLeft,
  FaChevronRight,
  FaPause,
  FaPlay,
} from "react-icons/fa";

import gsap from "gsap";

import Logo from "../../../assets/LogosemFundo.png";
import { beforeAfterItems } from "../../utils/beforeAfterImages";

import "../../../Styles/projectGallery.css";

const AUTOPLAY_DELAY = 5500;

const EMBLA_OPTIONS = {
  loop: true,
  align: "start",
  containScroll: "trimSnaps",
  dragFree: false,
  skipSnaps: false,
  slidesToScroll: 1,
  duration: 32,
};

export default function ProjectGallery() {
  const sectionRef = useRef(null);
  const wasPlayingBeforeLeavingRef = useRef(true);

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: AUTOPLAY_DELAY,
        playOnInit: true,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        stopOnFocusIn: true,
      }),
    [],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    EMBLA_OPTIONS,
    [autoplayPlugin],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);

  const items = useMemo(() => {
    if (!Array.isArray(beforeAfterItems)) {
      return [];
    }

    return beforeAfterItems.filter(
      (item) => item?.before && item?.after,
    );
  }, []);

  const synchronizeCarousel = useCallback((api) => {
    if (!api) {
      return;
    }

    setSelectedIndex(api.selectedScrollSnap());
    setScrollSnaps(api.scrollSnapList());
  }, []);

  const scrollPrevious = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const toggleAutoplay = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    const autoplay = emblaApi.plugins().autoplay;

    if (!autoplay) {
      return;
    }

    if (autoplay.isPlaying()) {
      autoplay.stop();
      return;
    }

    autoplay.play();
  }, [emblaApi]);

  const handleKeyboardNavigation = useCallback(
    (event) => {
      if (!emblaApi) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        emblaApi.scrollPrev();
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        emblaApi.scrollNext();
      }
    },
    [emblaApi],
  );

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reducedMotion) {
      return undefined;
    }

    const context = gsap.context(() => {
      const timeline = gsap.timeline({
        defaults: {
          ease: "power3.out",
        },
      });

      timeline
        .fromTo(
          ".before-after-brand",
          {
            opacity: 0,
            y: -28,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.85,
          },
        )
        .fromTo(
          ".before-after-heading",
          {
            opacity: 0,
            y: 22,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
          },
          "-=0.45",
        )
        .fromTo(
          ".before-after-card",
          {
            opacity: 0,
            y: 38,
            scale: 0.97,
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.9,
          },
          "-=0.4",
        )
        .fromTo(
          ".before-after-concepts",
          {
            opacity: 0,
            y: 18,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.65,
          },
          "-=0.4",
        );
    }, section);

    return () => {
      context.revert();
    };
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }

    const handleSelect = (api) => {
      synchronizeCarousel(api);
    };

    const handleReInit = (api) => {
      synchronizeCarousel(api);
    };

    emblaApi.on("select", handleSelect);
    emblaApi.on("reInit", handleReInit);

    const frameId = window.requestAnimationFrame(() => {
      synchronizeCarousel(emblaApi);
    });

    return () => {
      window.cancelAnimationFrame(frameId);

      emblaApi.off("select", handleSelect);
      emblaApi.off("reInit", handleReInit);
    };
  }, [emblaApi, synchronizeCarousel]);

  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }

    const handleAutoplayPlay = () => {
      setIsPlaying(true);
    };

    const handleAutoplayStop = () => {
      setIsPlaying(false);
    };

    emblaApi.on("autoplay:play", handleAutoplayPlay);
    emblaApi.on("autoplay:stop", handleAutoplayStop);

    const frameId = window.requestAnimationFrame(() => {
      const autoplay = emblaApi.plugins().autoplay;

      setIsPlaying(Boolean(autoplay?.isPlaying()));
    });

    return () => {
      window.cancelAnimationFrame(frameId);

      emblaApi.off("autoplay:play", handleAutoplayPlay);
      emblaApi.off("autoplay:stop", handleAutoplayStop);
    };
  }, [emblaApi]);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section || !emblaApi) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const autoplay = emblaApi.plugins().autoplay;

        if (!autoplay) {
          return;
        }

        if (!entry.isIntersecting) {
          wasPlayingBeforeLeavingRef.current =
            autoplay.isPlaying();

          autoplay.stop();
          return;
        }

        if (
          wasPlayingBeforeLeavingRef.current &&
          document.visibilityState === "visible"
        ) {
          autoplay.play();
        }
      },
      {
        threshold: 0.2,
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, [emblaApi]);

  if (items.length === 0) {
    return (
      <section className="before-after-gallery">
        <div className="before-after-empty">
          <p>
            Nenhuma imagem de antes e depois foi encontrada.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="before-after-gallery"
      aria-labelledby="before-after-title"
    >
      <div className="before-after-shell">
        <header className="before-after-header">
          <div className="before-after-brand">
            <img
              src={Logo}
              alt="Dra. Fabiana Catarino"
              className="before-after-logo"
              width="360"
              height="170"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="before-after-heading">
            <span className="before-after-eyebrow">
              Transformações reais
            </span>

            <h2
              id="before-after-title"
              className="before-after-title"
            >
              Antes e depois
            </h2>

            <p className="before-after-description">
              Resultados que valorizam a beleza natural,
              respeitando a individualidade de cada paciente.
            </p>
          </div>
        </header>

        <div
          className="before-after-carousel-wrapper"
          onKeyDown={handleKeyboardNavigation}
        >
          <div
            className="before-after-card"
            role="region"
            aria-roledescription="carrossel"
            aria-label="Resultados de tratamentos estéticos"
          >
            <div
              ref={emblaRef}
              className="before-after-viewport"
            >
              <div className="before-after-track">
                {items.map((item, index) => {
                  const title =
                    item.title ??
                    `Resultado ${index + 1}`;

                  const isSelected =
                    selectedIndex === index;

                  return (
                    <div
                      key={item.id ?? `resultado-${index}`}
                      className={[
                        "before-after-slide",
                        isSelected
                          ? "before-after-slide--selected"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      role="group"
                      aria-roledescription="slide"
                      aria-label={`${index + 1} de ${
                        items.length
                      }`}
                      aria-hidden={!isSelected}
                    >
                      <article className="before-after-result">
                        <div className="before-after-images">
                          <figure className="before-after-figure">
                            <div className="before-after-image-frame">
                              <img
                                src={item.before}
                                alt={
                                  item.beforeAlt ??
                                  `Antes do tratamento: ${title}`
                                }
                                className="before-after-image"
                                loading={
                                  index === 0
                                    ? "eager"
                                    : "lazy"
                                }
                                fetchPriority={
                                  index === 0
                                    ? "high"
                                    : "auto"
                                }
                                decoding="async"
                                draggable="false"
                              />
                            </div>

                            <figcaption className="before-after-label">
                              Antes
                            </figcaption>
                          </figure>

                          <div
                            className="before-after-center-detail"
                            aria-hidden="true"
                          >
                            <span>✦</span>
                          </div>

                          <figure className="before-after-figure">
                            <div className="before-after-image-frame">
                              <img
                                src={item.after}
                                alt={
                                  item.afterAlt ??
                                  `Depois do tratamento: ${title}`
                                }
                                className="before-after-image"
                                loading={
                                  index === 0
                                    ? "eager"
                                    : "lazy"
                                }
                                fetchPriority={
                                  index === 0
                                    ? "high"
                                    : "auto"
                                }
                                decoding="async"
                                draggable="false"
                              />
                            </div>

                            <figcaption className="before-after-label">
                              Depois
                            </figcaption>
                          </figure>
                        </div>

                        {(item.title ||
                          item.description) && (
                          <div className="before-after-result-content">
                            {item.title && (
                              <h3 className="before-after-result-title">
                                {item.title}
                              </h3>
                            )}

                            {item.description && (
                              <p className="before-after-result-description">
                                {item.description}
                              </p>
                            )}
                          </div>
                        )}
                      </article>
                    </div>
                  );
                })}
              </div>
            </div>

            {items.length > 1 && (
              <>
                <button
                  type="button"
                  className="before-after-arrow before-after-arrow--previous"
                  aria-label="Resultado anterior"
                  onClick={scrollPrevious}
                >
                  <FaChevronLeft aria-hidden="true" />
                </button>

                <button
                  type="button"
                  className="before-after-arrow before-after-arrow--next"
                  aria-label="Próximo resultado"
                  onClick={scrollNext}
                >
                  <FaChevronRight aria-hidden="true" />
                </button>
              </>
            )}

            {scrollSnaps.length > 1 && (
              <div className="before-after-controls">
                <div
                  className="before-after-dots"
                  role="tablist"
                  aria-label="Selecionar resultado"
                >
                  {scrollSnaps.map((_, index) => (
                    <button
                      key={`resultado-dot-${index}`}
                      type="button"
                      role="tab"
                      aria-label={`Ir para resultado ${
                        index + 1
                      }`}
                      aria-selected={
                        selectedIndex === index
                      }
                      className={[
                        "before-after-dot",
                        selectedIndex === index
                          ? "before-after-dot--active"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => scrollTo(index)}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  className="before-after-autoplay"
                  aria-label={
                    isPlaying
                      ? "Pausar carrossel"
                      : "Iniciar carrossel"
                  }
                  onClick={toggleAutoplay}
                >
                  {isPlaying ? (
                    <FaPause aria-hidden="true" />
                  ) : (
                    <FaPlay aria-hidden="true" />
                  )}
                </button>
              </div>
            )}

            <p
              className="before-after-status"
              aria-live="polite"
            >
              Resultado {selectedIndex + 1} de{" "}
              {items.length}
            </p>
          </div>
        </div>

        <footer className="before-after-concepts">
          <p className="before-after-concepts-text">
            Harmonização
            <span aria-hidden="true">•</span>
            Naturalidade
            <span aria-hidden="true">•</span>
            Realce da sua essência
          </p>

          <div
            className="before-after-ornament"
            aria-hidden="true"
          >
            <span />
            <strong>✦</strong>
            <span />
          </div>

          <p className="before-after-disclaimer">
            Os resultados podem variar de acordo com as
            características individuais de cada paciente.
          </p>
        </footer>
      </div>
    </section>
  );
}